import { useState } from "react";

export type SortDirection = "asc" | "desc";

export interface TableSort<K extends string> {
  sortKey: K;
  direction: SortDirection;
  /** Re-sorts by `key`, or flips direction when `key` is already active. */
  handleSort: (key: K) => void;
}

/**
 * Click-to-sort state for a table's column headers. `initialDirections` gives a
 * column the direction that puts its interesting rows first, so the first click
 * lands on the useful order rather than the alphabetical one.
 */
export function useTableSort<K extends string>(
  initialKey: K,
  initialDirection: SortDirection,
  initialDirections: Partial<Record<K, SortDirection>> = {}
): TableSort<K> {
  const [sortKey, setSortKey] = useState<K>(initialKey);
  const [direction, setDirection] = useState<SortDirection>(initialDirection);

  return {
    sortKey,
    direction,
    handleSort: (key) => {
      if (key === sortKey) {
        setDirection((current) => (current === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setDirection(initialDirections[key] ?? "asc");
      }
    },
  };
}

/** `1` for ascending, `-1` for descending. */
export const sortFactor = (direction: SortDirection): 1 | -1 => (direction === "asc" ? 1 : -1);

/**
 * Numeric compare that keeps nulls at the bottom in both directions: a result with
 * no score has not scored zero, and filing unknowns in among the worst failures
 * buries the set a reviewer came for.
 *
 * The direction factor is applied inside, to the value comparison only — a caller
 * that multiplied the result would flip the nulls to the top on a descending sort.
 */
export function compareNullsLast(a: number | null, b: number | null, factor: 1 | -1): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return factor * (a - b);
}
